import { FC, useEffect, useState } from 'react';
import { CareMapDrawer } from '../caremap/CareMapDrawer';
import { Redirect, Route } from 'react-router';
import { TrackPageView } from '../metrics/TrackPageView';
import { SupportGroupMemberList } from '../support-group/SupportGroupMemberList';
import { CareMapNotesView } from '../notes/CareMapNotesView';
import { AddOrEditNoteUsingCareMapDrawer } from '../notes/AddOrEditNoteUsingCareMapDrawer';
import { ViewNoteUsingCareMapDrawer } from '../notes/view-note/ViewNoteUsingCareMapDrawer';
import { ISupportGroup, ISupportGroupKey } from '../caremap/ICareMap';
import { AppointmentsView } from '../appointments/AppointmentsView';
import { AppointmentEditorCareMapDrawer } from '../appointments/AppointmentEditorCareMapDrawer';
import { SupportGroupMemberView } from '../support-group/SupportGroupMemberView';
import { SupportGroupMemberEdit } from '../support-group/SupportGroupMemberEdit';
import { HelpCenterArticlesView } from '../support/help-center/HelpCenterArticlesView';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { useDismissOverlays } from '../utils/useDismissOverlays';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { BlockUntilActiveCaremap } from '../ActiveCareMapProvider';

const ShowSupportGroup: React.FC<{ supportGroupKey: ISupportGroupKey }> = ({ supportGroupKey }) => {
    const [isRelevant, setIsRelevant] = useState<boolean>();

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    useEffect(() => {
        const group =  careMapState.getActiveCaremap()![supportGroupKey] as ISupportGroup;
        setIsRelevant(group.isRelevant);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // if we're not supposed to show it, don't allow a deep link to it
    if (isRelevant === false) {
        return <Redirect to="/caremaps" />;
    }

    return (
        <>
            <TrackPageView page="Care Member List" meta={{ category: supportGroupKey }} />
            <SupportGroupMemberList supportGroupKey={supportGroupKey} />
        </>
    );
};

// add to active care map
const AddSupportGroup: React.FC<{ supportGroupKey: ISupportGroupKey }> = ({ supportGroupKey }) => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    useEffect(() => {
        const group =  careMapState.getActiveCaremap()![supportGroupKey] as ISupportGroup;

        if (!group.isRelevant){
            careMapState.addOrUpdateSupportGroup({ supportGroupKey });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Redirect to={`/caremaps/${careMapState.getActiveCaremap()!.careMapId}/drawer/support-group/${supportGroupKey}`} />
    );
};

export const SupportGroupRouter: FC = () => {
    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));

    const dismissOverlays = useDismissOverlays();

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    return (
        <>
            <CareMapDrawer
                basePath="/caremaps/:careMapId/drawer/support-group/:supportGroupKey"
            >
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/add" render={(routeProps) => {
                    return (
                        <BlockUntilActiveCaremap>
                            <AddSupportGroup supportGroupKey={routeProps.match.params.supportGroupKey as ISupportGroupKey} />
                        </BlockUntilActiveCaremap>
                    );
                }} />

                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey" render={(routeProps) => {
                    return (
                        <BlockUntilActiveCaremap>
                            <ShowSupportGroup supportGroupKey={routeProps.match.params.supportGroupKey as ISupportGroupKey} />
                        </BlockUntilActiveCaremap>
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/notes" render={(routeProps) => {
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    const titlePrefix = careMapState.getSupportGroupLabels(supportGroupKey).title;

                    return (
                        <CareMapNotesView
                            titlePrefix={titlePrefix}
                            noteTarget={{ section: supportGroupKey }} />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/notes/:noteId" render={(routeProps) => {
                    const { supportGroupKey } = routeProps.match.params;
                    const { noteId } = routeProps.match.params;

                    // Handle add-note case - redirect to edit route
                    if (noteId === 'add-note') {
                        return (
                            <AddOrEditNoteUsingCareMapDrawer
                                noteId=""
                                defaultNoteTarget={{ section: supportGroupKey as ISupportGroupKey }}
                            />
                        );
                    }

                    return (
                        <ViewNoteUsingCareMapDrawer
                            noteId={noteId}
                            defaultNoteTarget={{ section: supportGroupKey as ISupportGroupKey }}
                        />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/notes/:noteId/edit" render={(routeProps) => {
                    const { supportGroupKey } = routeProps.match.params;
                    const { noteId } = routeProps.match.params;

                    return (
                        <AddOrEditNoteUsingCareMapDrawer
                            noteId={noteId}
                            defaultNoteTarget={{ section: supportGroupKey as ISupportGroupKey }}
                        />
                    );
                }} />

                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/appointments" render={(routeProps) => {
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    return <AppointmentsView supportGroupKey={supportGroupKey} />;
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/appointments/:appointmentId" render={(routeProps) => {
                    dismissOverlays();

                    const { appointmentId } = routeProps.match.params;
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;

                    return (
                        <AppointmentEditorCareMapDrawer
                            from={supportGroupKey}
                            supportGroupKey={supportGroupKey}
                            appointmentId={appointmentId}
                        />
                    );
                }} />

                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/member/:memberId" render={(routeProps) => {
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    const memberId = routeProps.match.params.memberId;
                    return (
                        <>
                            <TrackPageView page="Care Member Details" />
                            <SupportGroupMemberView supportGroupKey={supportGroupKey} memberId={memberId} />
                        </>
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/member/:memberId/edit" render={(routeProps) => {
                    if (isAnonymous) {
                        const careMapId = routeProps.match.params.careMapId;
                        return <Redirect to={`/caremaps/${careMapId}`} />;
                    }
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    const memberId = routeProps.match.params.memberId;
                    return (
                        <SupportGroupMemberEdit supportGroupKey={supportGroupKey} memberId={memberId} />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/add-member" render={(routeProps) => {
                    if (isAnonymous) {
                        const careMapId = routeProps.match.params.careMapId;
                        return <Redirect to={`/caremaps/${careMapId}`} />;
                    }
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    const searchParams = new URLSearchParams(routeProps.location.search);
                    return (
                        <SupportGroupMemberEdit supportGroupKey={supportGroupKey} autoSelectFromContacts={Boolean(searchParams.get('aafc'))} />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/member/:memberId/notes" render={(routeProps) => {
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    const titlePrefix = careMapState.getSupportGroupLabels(supportGroupKey).memberTitle;
                    const memberId = routeProps.match.params.memberId;

                    return (
                        <CareMapNotesView
                            titlePrefix={titlePrefix}
                            noteTarget={{ section: supportGroupKey, memberId: memberId }}
                        />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/member/:memberId/notes/:noteId" render={(routeProps) => {
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    const { memberId } = routeProps.match.params;
                    const { noteId } = routeProps.match.params;

                    // Handle add-note case - redirect to edit route
                    if (noteId === 'add-note') {
                        return (
                            <AddOrEditNoteUsingCareMapDrawer
                                noteId=""
                                defaultNoteTarget={{ section: supportGroupKey, memberId: memberId }}
                            />
                        );
                    }

                    return (
                        <ViewNoteUsingCareMapDrawer
                            noteId={noteId}
                            defaultNoteTarget={{ section: supportGroupKey, memberId: memberId }}
                        />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/member/:memberId/notes/:noteId/edit" render={(routeProps) => {
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    const { memberId } = routeProps.match.params;
                    const { noteId } = routeProps.match.params;

                    return (
                        <AddOrEditNoteUsingCareMapDrawer
                            noteId={noteId}
                            defaultNoteTarget={{ section: supportGroupKey, memberId: memberId }}
                        />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/member/:memberId/appointments" render={(routeProps) => {
                    const { memberId } = routeProps.match.params;
                    return <AppointmentsView memberId={memberId} />;
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/member/:memberId/appointments/:appointmentId" render={(routeProps) => {
                    dismissOverlays();

                    const { appointmentId } = routeProps.match.params;
                    const { memberId } = routeProps.match.params;
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;

                    return (
                        <AppointmentEditorCareMapDrawer
                            from={`${supportGroupKey} member`}
                            supportMemberId={memberId}
                            supportGroupKey={supportGroupKey}
                            appointmentId={appointmentId}
                        />
                    );
                }} />

                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/help-center/articles" render={(routeProps) => {
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    return <HelpCenterArticlesView supportGroupKey={supportGroupKey} />;
                }} />
            </CareMapDrawer>
        </>
    );
};
